/**
 * Incarcarea API-ului IFrame al YouTube, o singura data pe pagina.
 *
 * De ce nu se cheama direct din componenta: scriptul se adauga o singura data si
 * anunta gata printr-un callback GLOBAL (`onYouTubeIframeAPIReady`). Doua
 * componente care il pun fiecare pe al ei se suprascriu una pe alta, iar a doua
 * nu mai afla niciodata ca playerul e gata.
 *
 * ⚠️ Daca `window.YT` exista deja, NU se mai adauga nimic — asa poate o sonda sa
 * injecteze un API fals inainte de randare si sa masoare poarta de vizionare
 * fara nicio cerere in afara localhost-ului. Garda de origine ramane absoluta.
 */

export interface PlayerYouTube {
    getDuration: () => number;
    getCurrentTime: () => number;
    playVideo: () => void;
    /** Garda de salt (`gardaSaltInainte`). Optionala: un API fals injectat de o sonda poate lipsi. */
    seekTo?: (secunde: number, permiteDescarcare: boolean) => void;
    /** Viteza de redare (1 = normal); lipsa sau necitibila se trateaza ca 1. */
    getPlaybackRate?: () => number;
    destroy?: () => void;
}

/** Starile de care depinde ecranul; restul enumeratului nu ne intereseaza. */
export const STARE = { NEPORNIT: -1, TERMINAT: 0, RULEAZA: 1, PAUZA: 2 } as const;

const URL_API = 'https://www.youtube.com/iframe_api';

let promisiune: Promise<void> | null = null;

export function incarcaApiYouTube(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    const w = window as unknown as Record<string, unknown>;
    if (w.YT && (w.YT as { Player?: unknown }).Player) return Promise.resolve();
    if (promisiune) return promisiune;

    promisiune = new Promise<void>((resolve) => {
        const anterior = w.onYouTubeIframeAPIReady as (() => void) | undefined;
        w.onYouTubeIframeAPIReady = () => {
            if (typeof anterior === 'function') anterior();
            resolve();
        };
        const script = document.createElement('script');
        script.src = URL_API;
        script.async = true;
        document.head.appendChild(script);
    });
    return promisiune;
}

/**
 * Id-ul videoclipului dintr-un URL de YouTube. Intoarce `null` cand nu-l poate
 * scoate — apelantul afiseaza o eroare, nu un player gol care asteapta la
 * infinit.
 */
export function idVideoDinUrl(url: string): string | null {
    const text = String(url || '').trim();
    if (!text) return null;
    const potriviri = [
        /[?&]v=([A-Za-z0-9_-]{11})/,        // watch?v=...
        /youtu\.be\/([A-Za-z0-9_-]{11})/,   // youtu.be/...
        /\/embed\/([A-Za-z0-9_-]{11})/,     // /embed/...
    ];
    for (const tipar of potriviri) {
        const m = text.match(tipar);
        if (m) return m[1];
    }
    return /^[A-Za-z0-9_-]{11}$/.test(text) ? text : null;
}
